// History.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Box, Tabs, Tab } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import SalesHistoryGrid from './SalesHistoryGrid';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';

// Same search field style you use on inventory
const NoBorderTextField = styled(TextField)({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: 10,
    backgroundColor: '#f3f3f6',
    '& fieldset': { border: 'none' },
  },
  '& .MuiOutlinedInput-input': {
    color: '#333',
    fontSize: '1rem',
    fontWeight: 500,
    '&::placeholder': { color: '#888', opacity: 1 },
  },
});

// Tab config for history
const TAB_KEYS = [
  { key: 'ALL',     label: 'All' },
  { key: 'TODAY',   label: 'Today' },
  { key: 'LAST_7',  label: 'Last 7 Days' },
  { key: 'LAST_30', label: 'Last 30 Days' },
];

// Map tabs → API filter params (these go as query params to /sales)
const tabToFilter = (tabKey) => {
  switch (tabKey) {
    case 'TODAY':   return { range: 'today' };
    case 'LAST_7':  return { range: '7d' };
    case 'LAST_30': return { range: '30d' };
    default:        return {};
  }
};

// Allow deep-linking like ?range=7d
const queryToTab = (search) => {
  const q = new URLSearchParams(search);
  const r = (q.get('range') || '').toLowerCase();
  if (r === 'today') return 'TODAY';
  if (r === '7d')    return 'LAST_7';
  if (r === '30d')   return 'LAST_30';
  return 'ALL';
};

const History = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [tab, setTab] = useState(() => queryToTab(location.search));
  const [search, setSearch] = useState('');

  // reflect external URL changes (back/forward)
  useEffect(() => setTab(queryToTab(location.search)), [location.search]);

  const handleTabChange = (_e, value) => {
    setTab(value);
    const params = new URLSearchParams(location.search);
    switch (value) {
      case 'TODAY':   params.set('range', 'today'); break;
      case 'LAST_7':  params.set('range', '7d');    break;
      case 'LAST_30': params.set('range', '30d');   break;
      default:        params.delete('range');       break;
    }
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true }
    );
  };

  const filter = useMemo(() => tabToFilter(tab), [tab]);

  return (
    <Box sx={{ width: {sm:'90%', md:'100%'}, marginLeft:{xs:'8%', sm:'2%', md:'1%'}, textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: 1 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '90%',
          margin: 'auto',
          py: '15px',
          pb: '32px',
        }}
      >
        <Typography variant="h3" sx={{ fontSize: {
      xs: '1.6rem',  // mobile
      sm: '2rem',    // small tablets
      md: '2.5rem',  // desktop
    }, fontWeight: 600 }}>
          History
        </Typography>
        {/* Right side empty for now (you can later add Export etc.) */}
        <Box />
      </Box>

      {/* Search bar (same style as inventory search) */}
      <Box sx={{ width: '90%', margin: '0 auto 16px auto' }}>
        <NoBorderTextField
          placeholder="Search by ref no, payment, or medicine name…"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
      '& input::placeholder': {
        fontSize: { xs: '0.8rem', sm: '1rem' },
      },
    }}
        />
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          width: '90%',
          margin: 'auto',
          mb: 2,
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        {TAB_KEYS.map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} />
        ))}
      </Tabs>

      {/* Grid */}
      <Box
        sx={{
          width: '90%',
          margin: 'auto',
          backgroundColor: '#f9fafb',
          pt: 2,
          pb: 2,
        }}
      >
        <SalesHistoryGrid filter={filter} search={search} />
      </Box>
    </Box>
  );
};

export default History;
